import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  X, ChevronRight, Heart, Send, Megaphone, QrCode, ArrowLeft,
  Sparkles, Type, Image as ImageIcon, Video, Upload, Loader2, Eye,
  CheckCircle2, AlertCircle, Trash2, Wand2, MapPin, ExternalLink,
  Wifi, WifiOff, Link2,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Renderiza um texto de mensagem com URLs convertidas em <a> clicáveis (estilo WhatsApp).
 * Detecta http(s)://… e converte tudo o resto em texto puro, preservando quebras de linha
 * via `whitespace-pre-wrap` no container pai.
 */
function renderMessageWithLinks(text) {
  if (!text) return null;
  const URL_RE = /(https?:\/\/[^\s<>"']+)/gi;
  const parts = String(text).split(URL_RE);
  return parts.map((part, idx) => {
    if (idx % 2 === 1) {
      return (
        <a
          key={idx}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#0a84ff] underline break-all hover:opacity-80"
          data-testid="wa-preview-link"
        >
          {part}
        </a>
      );
    }
    return <React.Fragment key={idx}>{part}</React.Fragment>;
  });
}

/* ============================================================================
 *  PREMIUM WHATSAPP SEND FLOW
 *  - 1 botão "Enviar em massa" no dashboard abre este fluxo
 *  - Passo 1: escolher tipo de envio (4 cards premium)
 *  - Passo 2: composer premium (mensagem + variáveis + mídia + preview)
 *  - Sem branding externo, look WhatsApp real
 * ========================================================================= */

const SEND_TYPES = [
  {
    id: "save_the_date",
    label: "Save the Date",
    description: "Envie uma prévia elegante do evento — sem link de confirmação.",
    icon: Heart,
    accent: "from-rose-500/15 to-rose-500/0",
    ring: "ring-rose-500/20",
    iconBg: "bg-rose-500/10 text-rose-600",
    hasLink: false,
    needsMedia: false,
    sourceMedia: "save_the_date",
  },
  {
    id: "invitation",
    label: "Convite digital",
    description: "Envie o convite com link individual e confirmação de presença.",
    icon: Send,
    accent: "from-emerald-500/15 to-emerald-500/0",
    ring: "ring-emerald-500/20",
    iconBg: "bg-emerald-500/10 text-emerald-600",
    hasLink: true,
    needsMedia: false,
    sourceMedia: "invitation",
  },
  {
    id: "notice",
    label: "Aviso / Comunicado",
    description: "Envie lembretes e comunicados gerais — apenas mensagem.",
    icon: Megaphone,
    accent: "from-amber-500/15 to-amber-500/0",
    ring: "ring-amber-500/20",
    iconBg: "bg-amber-500/10 text-amber-600",
    hasLink: false,
    needsMedia: false,
    sourceMedia: null,
  },
  {
    id: "qrcode",
    label: "QR Code de entrada",
    description: "Envie o QR Code individual — apenas para confirmados.",
    icon: QrCode,
    accent: "from-sky-500/15 to-sky-500/0",
    ring: "ring-sky-500/20",
    iconBg: "bg-sky-500/10 text-sky-600",
    hasLink: true,
    needsMedia: false,
    filterConfirmedOnly: true,
    sourceMedia: null,
  },
  {
    id: "checkout_qr",
    label: "QR Code de retirada",
    description: "Envie o QR de check-out das crianças — apenas para quem fez check-in.",
    icon: QrCode,
    accent: "from-violet-500/15 to-violet-500/0",
    ring: "ring-violet-500/20",
    iconBg: "bg-violet-500/10 text-violet-600",
    hasLink: false,
    needsMedia: false,
    filterCheckedInOnly: true,
    sourceMedia: null,
  },
];

const VARS = [
  { token: "{nome}",             label: "Nome do convidado" },
  { token: "{evento}",           label: "Nome do evento" },
  { token: "{data}",             label: "Data" },
  { token: "{local}",            label: "Local" },
  { token: "{mesa}",             label: "Mesa" },
  { token: "{link_confirmacao}", label: "Link" },
];

const DEFAULT_TEMPLATES = {
  save_the_date: `Olá {nome} ✨\n\nGuarde a data: {evento} em {data}.\n\nEm breve enviaremos seu convite oficial. 💛`,
  invitation: `Olá {nome},\n\nÉ com grande alegria que convidamos você para {evento}.\n📅 {data}\n📍 {local}\n\nConfirme sua presença: {link_confirmacao}\n\nContamos com você! 💛`,
  qrcode: `Olá {nome}!\n\nSeu QR Code para entrada em {evento}: {link_confirmacao}\n\nNos vemos no dia! 🎉`,
  notice: `Olá {nome},\n\nTemos um aviso importante sobre {evento}.\n\n`,
};

function useDebounced(value, delay = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

/* ============================================================================
 *  ROOT MODAL
 * ========================================================================= */
export default function WhatsAppSendFlow({ open, onClose, selectedInvites, wedding, onSent }) {
  const [step, setStep] = useState("pick"); // 'pick' | 'compose'
  const [chosen, setChosen] = useState(null);
  const [bulkSending, setBulkSending] = useState(false);

  const handlePick = async (t) => {
    setChosen(t);
    setStep("compose");
  };

  useEffect(() => {
    if (!open) {
      // pequeno delay pra evitar piscar antes do fade-out
      const t = setTimeout(() => { setStep("pick"); setChosen(null); }, 250);
      return () => clearTimeout(t);
    }
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center bg-brand-deep/60 backdrop-blur-sm"
          onClick={onClose}
          data-testid="wa-send-flow-overlay"
        >
          <motion.div
            initial={{ y: 40, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 30, opacity: 0, scale: 0.98 }}
            transition={{ type: "spring", damping: 26, stiffness: 280 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full sm:max-w-3xl bg-white sm:rounded-3xl rounded-t-3xl shadow-2xl overflow-hidden max-h-[94vh] flex flex-col"
            data-testid="wa-send-flow-modal"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-100">
              <div className="flex items-center gap-3">
                {step === "compose" && (
                  <button
                    type="button"
                    onClick={() => setStep("pick")}
                    className="w-9 h-9 rounded-full bg-zinc-100 hover:bg-zinc-200 flex items-center justify-center transition-colors"
                    data-testid="wa-flow-back"
                  >
                    <ArrowLeft size={16} className="text-zinc-700" />
                  </button>
                )}
                <div>
                  <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-400 font-medium">Envio em massa</p>
                  <h2 className="text-[18px] font-semibold text-zinc-900 tracking-tight">
                    {step === "pick" ? "O que deseja enviar?" : chosen?.label}
                  </h2>
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="w-9 h-9 rounded-full bg-zinc-100 hover:bg-zinc-200 flex items-center justify-center transition-colors"
                data-testid="wa-flow-close"
              >
                <X size={16} className="text-zinc-700" />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto">
              {step === "pick" && (
                <TypePicker
                  selectedCount={selectedInvites.length}
                  onPick={handlePick}
                />
              )}
              {step === "compose" && chosen && (
                <Composer
                  type={chosen}
                  selectedInvites={selectedInvites}
                  wedding={wedding}
                  onClose={onClose}
                  onSent={onSent}
                />
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* ============================================================================
 *  STEP 1 — TYPE PICKER
 * ========================================================================= */
function TypePicker({ selectedCount, onPick }) {
  return (
    <div className="p-6 space-y-5" data-testid="wa-type-picker">
      <p className="text-[13px] text-zinc-500 leading-relaxed">
        <span className="font-semibold text-zinc-900">{selectedCount}</span>{" "}
        convidado{selectedCount === 1 ? "" : "s"} selecionado{selectedCount === 1 ? "" : "s"}.
        Escolha o tipo de mensagem premium que deseja enviar.
      </p>

      <div className="grid sm:grid-cols-2 gap-3">
        {SEND_TYPES.map((t) => {
          const Icon = t.icon;
          return (
            <motion.button
              key={t.id}
              type="button"
              onClick={() => onPick(t)}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.985 }}
              transition={{ type: "spring", stiffness: 400, damping: 26 }}
              className={`group relative text-left rounded-2xl border border-zinc-200/80 bg-white p-5 overflow-hidden ring-1 ring-transparent hover:${t.ring} hover:border-zinc-300 transition-all`}
              data-testid={`wa-type-${t.id}`}
            >
              <div className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${t.accent} opacity-0 group-hover:opacity-100 transition-opacity`} />
              <div className="relative flex items-start justify-between gap-3">
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${t.iconBg}`}>
                  <Icon size={20} />
                </div>
                <ChevronRight size={16} className="text-zinc-300 mt-3 group-hover:text-zinc-700 group-hover:translate-x-0.5 transition-all" />
              </div>
              <div className="relative mt-4">
                <h3 className="text-[15px] font-semibold text-zinc-900 tracking-tight">{t.label}</h3>
                <p className="mt-1 text-[12.5px] text-zinc-500 leading-relaxed">{t.description}</p>
                {t.filterConfirmedOnly && (
                  <div className="mt-3 inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-brand-deep/[0.03] text-[10.5px] text-zinc-600 font-medium">
                    <CheckCircle2 size={11} /> Apenas confirmados receberão
                  </div>
                )}
                {t.filterCheckedInOnly && (
                  <div className="mt-3 inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-violet-500/[0.06] text-[10.5px] text-violet-700 font-medium">
                    <CheckCircle2 size={11} /> Apenas quem fez check-in receberá
                  </div>
                )}
              </div>
            </motion.button>
          );
        })}
      </div>

      <div className="mt-2 rounded-xl border border-zinc-100 bg-zinc-50/60 px-4 py-3 text-[11.5px] text-zinc-500 leading-relaxed">
        <span className="font-medium text-zinc-700">Privado e individualizado.</span> Cada convidado recebe a mensagem com o próprio nome, mesa e link único — automaticamente.
      </div>
    </div>
  );
}

/* ============================================================================
 *  STEP 2 — COMPOSER
 * ========================================================================= */
function Composer({ type, selectedInvites, wedding, onClose, onSent }) {
  const sampleId = selectedInvites[0];
  const messageType = type.id;
  const navigate = useNavigate();

  // Estado da personalização
  const [customize, setCustomize] = useState(false);
  const [template, setTemplate] = useState(DEFAULT_TEMPLATES[messageType] || "");
  // Mídia: existente do evento, ou upload novo
  const existingMedia = useMemo(() => resolveExistingMedia(messageType, wedding), [messageType, wedding]);
  const [media, setMedia] = useState(existingMedia); // { url, type } | null
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  // Localização
  const [location, setLocation] = useState(null); // { mode: 'maps_link'|'native', ...data } | null
  const [locMeta, setLocMeta] = useState(null);   // dados do endpoint /api/wa/event-location

  // WhatsApp status & capabilities
  const [waStatus, setWaStatus] = useState(null); // { connected, state, ... }
  const [waCaps, setWaCaps] = useState(null);

  // Sending
  const [sending, setSending] = useState(false);
  const [eligibility, setEligibility] = useState(null);
  const [loadingEligibility, setLoadingEligibility] = useState(false);

  // Preview
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(false);
  const debouncedTemplate = useDebounced(customize ? template : null, 300);
  const debouncedMedia = useDebounced(media, 200);
  const debouncedLocation = useDebounced(location, 200);

  // textarea ref para insert var
  const taRef = useRef(null);

  // Status WhatsApp + capabilities + meta da localização (one-shot ao montar)
  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };
    Promise.allSettled([
      axios.get(`${API}/wa/status`, { headers }),
      axios.get(`${API}/wa/capabilities`, { headers }),
      axios.get(`${API}/wa/event-location`, { headers }),
    ]).then(([s, c, l]) => {
      if (cancelled) return;
      if (s.status === "fulfilled") setWaStatus(s.value.data);
      if (c.status === "fulfilled") setWaCaps(c.value.data);
      if (l.status === "fulfilled") setLocMeta(l.value.data);
    });
    return () => { cancelled = true; };
  }, []);

  // Carrega elegibilidade (especialmente QR code)
  useEffect(() => {
    let cancelled = false;
    setLoadingEligibility(true);
    const token = localStorage.getItem("token");
    axios.post(`${API}/wa/send-preview`,
      { invite_ids: selectedInvites, message_type: messageType },
      { headers: { Authorization: `Bearer ${token}` } }
    ).then((res) => { if (!cancelled) setEligibility(res.data); })
     .catch(() => { if (!cancelled) setEligibility(null); })
     .finally(() => { if (!cancelled) setLoadingEligibility(false); });
    return () => { cancelled = true; };
  }, [messageType, selectedInvites]);

  // Render preview com debounce
  useEffect(() => {
    if (!sampleId) return;
    let cancelled = false;
    setPreviewLoading(true);
    setPreviewError(false);
    const token = localStorage.getItem("token");
    axios.post(`${API}/wa/render-preview`,
      {
        invite_id: sampleId,
        message_type: messageType,
        custom_message_template: customize ? template : null,
        custom_media_url: media?.url || null,
        custom_media_type: media?.type || null,
        attach_media: !!media,
        location_mode: location?.mode || null,
        location_address: location?.address || null,
        location_latitude: location?.latitude ?? null,
        location_longitude: location?.longitude ?? null,
        location_name: location?.name || null,
      },
      { headers: { Authorization: `Bearer ${token}` } }
    ).then((res) => { if (!cancelled) { setPreview(res.data); setPreviewError(false); } })
     .catch(() => { if (!cancelled) { setPreview(null); setPreviewError(true); } })
     .finally(() => { if (!cancelled) setPreviewLoading(false); });
    return () => { cancelled = true; };
  }, [sampleId, messageType, debouncedTemplate, debouncedMedia, debouncedLocation, customize]);

  const insertVar = (token) => {
    const el = taRef.current;
    if (!el) {
      setTemplate((t) => t + token);
      return;
    }
    const start = el.selectionStart ?? template.length;
    const end = el.selectionEnd ?? template.length;
    const next = template.slice(0, start) + token + template.slice(end);
    setTemplate(next);
    setTimeout(() => { el.focus(); el.selectionStart = el.selectionEnd = start + token.length; }, 0);
  };

  const handlePickFile = () => fileRef.current?.click();

  const handleUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 12 * 1024 * 1024) {
      toast.error("Arquivo muito grande (máx 12MB).");
      e.target.value = "";
      return;
    }
    setUploading(true);
    try {
      const token = localStorage.getItem("token");
      const fd = new FormData();
      fd.append("file", f);
      const res = await axios.post(`${API}/wa/upload-media`, fd, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      setMedia({ url: res.data.url, type: res.data.type });
      toast.success("Mídia anexada");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha no upload");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleSend = async () => {
    // P0: bloquear envio se WhatsApp desconectado, com CTA pra reconectar
    if (waStatus && !waStatus.connected) {
      toast.error("WhatsApp não está conectado. Reconecte antes de enviar.", {
        action: { label: "Conectar", onClick: () => { onClose?.(); navigate("/whatsapp"); } },
        duration: 6000,
      });
      return;
    }
    setSending(true);
    const loading = toast.loading(`Enviando para ${eligibility?.eligible_count ?? selectedInvites.length} convidado(s)...`);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API}/wa/send-bulk`,
        {
          invite_ids: selectedInvites,
          message_type: messageType,
          custom_message_template: customize ? template : null,
          custom_media_url: media?.url || null,
          custom_media_type: media?.type || null,
          attach_media: !!media,
          location_mode: location?.mode || null,
          location_address: location?.address || null,
          location_latitude: location?.latitude ?? null,
          location_longitude: location?.longitude ?? null,
          location_name: location?.name || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.dismiss(loading);
      const sent = res.data.sent || 0;
      const failed = res.data.failed || 0;
      const lastError = res.data.last_error;
      const mockLabel = res.data.mock ? " (Simulado)" : "";
      if (sent > 0) toast.success(`${sent} mensagem(ns) enviada(s) com sucesso!${mockLabel}`);
      if (failed > 0) {
        let description = "Verifique a conexão e tente novamente.";
        if (lastError) {
          if (/Bad Request|400|sendMedia|media|url|Could not/i.test(lastError)) {
            description = "Falha ao processar a mídia. Verifique se o arquivo é compatível e tente novamente.";
          } else if (/timeout|TimeoutException/i.test(lastError)) {
            description = "Tempo esgotado. Conexão lenta com o WhatsApp.";
          } else if (/desconectado|not connected/i.test(lastError)) {
            description = "WhatsApp desconectado durante o envio.";
          } else {
            description = lastError.slice(0, 180);
          }
        }
        toast.error(`${failed} envio(s) falharam`, { description, duration: 7000 });
      }
      if (sent === 0 && failed === 0) toast.info(res.data.message || "Nenhuma mensagem enviada.");
      onSent?.(res.data);
      onClose?.();
    } catch (err) {
      toast.dismiss(loading);
      const status = err.response?.status;
      const detail = err.response?.data?.detail || "Erro ao enviar.";
      if (status === 400 && /desconectado|nao conectado|não conectado/i.test(detail)) {
        toast.error("WhatsApp desconectado.", {
          description: "Reconecte sua conta antes de enviar.",
          action: { label: "Conectar", onClick: () => { onClose?.(); navigate("/whatsapp"); } },
          duration: 7000,
        });
      } else if (status === 400 && /nao configurado|não configurado/i.test(detail)) {
        toast.error("WhatsApp não configurado.", {
          description: "Conecte sua conta para começar a enviar.",
          action: { label: "Configurar", onClick: () => { onClose?.(); navigate("/whatsapp"); } },
          duration: 7000,
        });
      } else {
        toast.error(detail);
      }
    } finally {
      setSending(false);
    }
  };

  const skipCount =
    (eligibility?.skipped_pending || 0) +
    (eligibility?.skipped_declined || 0) +
    (eligibility?.skipped_confirmed || 0) +
    (eligibility?.skipped_no_phone || 0);
  const eligibleCount = eligibility?.eligible_count ?? selectedInvites.length;

  return (
    <div className="grid lg:grid-cols-[1fr_360px] divide-y lg:divide-y-0 lg:divide-x divide-zinc-100">
      {/* LEFT — Editor */}
      <div className="p-6 space-y-5" data-testid="wa-composer-editor">
        {/* WhatsApp connection banner */}
        {waStatus && !waStatus.connected && (
          <div className="rounded-2xl border border-amber-200 bg-amber-50/70 p-4 flex items-start gap-3" data-testid="wa-disconnected-banner">
            <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
              <WifiOff size={16} className="text-amber-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-semibold text-amber-900 tracking-tight">WhatsApp desconectado</p>
              <p className="text-[11.5px] text-amber-800/80 leading-relaxed mt-0.5">
                Para enviar mensagens premium aos seus convidados, conecte sua conta do WhatsApp Business primeiro.
              </p>
            </div>
            <button
              type="button"
              onClick={() => { onClose?.(); navigate("/whatsapp"); }}
              className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-[11.5px] font-semibold transition-colors"
              data-testid="wa-disconnected-cta"
            >
              <Wifi size={12} /> Conectar
            </button>
          </div>
        )}
        {waStatus && waStatus.connected && (
          <div className="flex items-center gap-2 text-[10.5px] text-emerald-700 font-medium" data-testid="wa-connected-pill">
            <span className="relative flex h-1.5 w-1.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500"></span>
            </span>
            Conectado como {waStatus.name || waStatus.phone || "WhatsApp"}
          </div>
        )}

        {/* Type badge + eligibility */}
        <div className="rounded-2xl border border-zinc-200/70 p-4 bg-gradient-to-br from-white to-zinc-50/40">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${type.iconBg}`}>
                <type.icon size={18} />
              </div>
              <div>
                <p className="text-[13px] font-semibold text-zinc-900 tracking-tight">{type.label}</p>
                <p className="text-[11.5px] text-zinc-500">{type.description}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-[10.5px] uppercase tracking-wider text-zinc-400 font-medium">elegíveis</p>
              <p className="text-[20px] font-semibold text-zinc-900 leading-none">
                {loadingEligibility ? "—" : eligibleCount}
              </p>
            </div>
          </div>

          {!loadingEligibility && skipCount > 0 && (
            <div className="mt-3 flex items-start gap-2 px-3 py-2 rounded-lg bg-amber-50/60 border border-amber-100">
              <AlertCircle size={13} className="text-amber-500 mt-0.5 shrink-0" />
              <p className="text-[11.5px] text-amber-700 leading-relaxed">
                {messageType === "qrcode"
                  ? "Somente convidados com presença confirmada receberão o QR Code."
                  : "Alguns convidados serão ignorados (sem telefone)."}
                {" · "}
                <span className="font-medium">{skipCount}</span> ignorado(s).
              </p>
            </div>
          )}
        </div>

        {/* Toggle personalizar */}
        <div className="rounded-2xl border border-zinc-200/70 overflow-hidden">
          <div className="flex items-center justify-between gap-3 px-4 py-3.5 bg-white">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-lg bg-brand-deep text-white flex items-center justify-center">
                <Wand2 size={14} />
              </div>
              <div>
                <p className="text-[13px] font-semibold text-zinc-900 tracking-tight">Mensagem</p>
                <p className="text-[11px] text-zinc-500">
                  {customize ? "Personalizada · você está editando" : "Padrão liventy · pronta pra enviar"}
                </p>
              </div>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={customize}
              onClick={() => setCustomize((v) => !v)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${customize ? "bg-brand-deep" : "bg-zinc-200"}`}
              data-testid="wa-composer-toggle-custom"
            >
              <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${customize ? "translate-x-5" : "translate-x-0.5"}`} />
            </button>
          </div>

          {customize && (
            <div className="px-4 pt-1 pb-4 space-y-3 border-t border-zinc-100">
              <textarea
                ref={taRef}
                value={template}
                onChange={(e) => setTemplate(e.target.value)}
                rows={8}
                placeholder="Olá {nome}, ..."
                className="w-full px-3 py-2.5 text-[13.5px] rounded-xl border border-zinc-200 bg-white text-zinc-800 placeholder:text-zinc-300 focus:outline-none focus:border-zinc-900 focus:ring-2 focus:ring-zinc-900/5 transition-all resize-none leading-relaxed"
                data-testid="wa-composer-textarea"
              />
              <div>
                <p className="text-[10.5px] uppercase tracking-wider font-medium text-zinc-400 mb-1.5">
                  Variáveis · clique para inserir
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {VARS.map((v) => (
                    <button
                      key={v.token}
                      type="button"
                      onClick={() => insertVar(v.token)}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-mono bg-zinc-100 text-zinc-700 hover:bg-brand-deep hover:text-white transition-colors"
                      title={v.label}
                      data-testid={`wa-var-${v.token.replace(/[{}]/g, "")}`}
                    >
                      {v.token}
                    </button>
                  ))}
                </div>
                <p className="text-[10.5px] text-zinc-400 mt-2 leading-relaxed">
                  Cada variável é substituída individualmente para cada convidado.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Mídia */}
        {messageType !== "qrcode" && (
          <div className="rounded-2xl border border-zinc-200/70 p-4 bg-white space-y-3" data-testid="wa-composer-media">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[13px] font-semibold text-zinc-900 tracking-tight">Anexar mídia</p>
                <p className="text-[11px] text-zinc-500">
                  {media ? "1 mídia anexada" : "Imagem, GIF ou vídeo curto (opcional)"}
                </p>
              </div>
              {media && (
                <button
                  type="button"
                  onClick={() => setMedia(null)}
                  className="text-[11.5px] text-zinc-500 hover:text-rose-600 inline-flex items-center gap-1"
                  data-testid="wa-composer-media-remove"
                >
                  <Trash2 size={12} /> remover
                </button>
              )}
            </div>

            {media ? (
              <MediaThumb media={media} />
            ) : (
              <>
                <button
                  type="button"
                  onClick={handlePickFile}
                  disabled={uploading}
                  className="w-full rounded-xl border-2 border-dashed border-zinc-200 hover:border-zinc-400 bg-zinc-50/50 hover:bg-zinc-50 transition-all px-4 py-5 flex flex-col items-center justify-center gap-1.5 text-zinc-600"
                  data-testid="wa-composer-media-upload"
                >
                  {uploading ? <Loader2 size={18} className="animate-spin" /> : <Upload size={18} />}
                  <span className="text-[12.5px] font-medium">
                    {uploading ? "Enviando..." : "Selecionar arquivo"}
                  </span>
                  <span className="text-[10.5px] text-zinc-400">JPG · PNG · WEBP · GIF · MP4 · até 12MB</span>
                </button>
                {existingMedia && (
                  <button
                    type="button"
                    onClick={() => setMedia(existingMedia)}
                    className="w-full text-[11.5px] text-zinc-700 underline-offset-2 hover:underline inline-flex items-center justify-center gap-1.5"
                    data-testid="wa-composer-media-use-existing"
                  >
                    <Sparkles size={11} /> Usar mídia já cadastrada do evento
                  </button>
                )}
              </>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif,video/mp4,video/quicktime,video/webm"
              className="hidden"
              onChange={handleUpload}
            />
          </div>
        )}

        {/* Localização */}
        {messageType !== "qrcode" && (
          <LocationCard
            locMeta={locMeta}
            waCaps={waCaps}
            value={location}
            onChange={setLocation}
            onGoToSettings={() => { onClose?.(); navigate("/configuracoes"); }}
          />
        )}

        {/* Send button */}
        <div className="pt-1">
          <button
            type="button"
            onClick={handleSend}
            disabled={sending || eligibleCount === 0 || (waStatus && !waStatus.connected)}
            className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-brand-deep hover:bg-brand disabled:bg-zinc-300 disabled:cursor-not-allowed text-white py-3.5 px-5 text-[14px] font-semibold tracking-tight transition-all shadow-sm hover:shadow-md"
            data-testid="wa-composer-send"
          >
            {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            {sending
              ? "Enviando..."
              : (waStatus && !waStatus.connected)
                ? "Conecte o WhatsApp para enviar"
                : `Enviar para ${eligibleCount} convidado${eligibleCount === 1 ? "" : "s"}`}
          </button>
          {eligibleCount === 0 && !loadingEligibility && (
            <p className="text-center text-[11.5px] text-zinc-400 mt-2">
              Nenhum convidado elegível para este envio.
            </p>
          )}
        </div>
      </div>

      {/* RIGHT — Preview WhatsApp */}
      <div className="bg-zinc-50/50 p-6 flex flex-col" data-testid="wa-composer-preview">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[10.5px] uppercase tracking-[0.18em] font-medium text-zinc-400">Pré-visualização</p>
          {previewLoading && <Loader2 size={12} className="animate-spin text-zinc-400" />}
        </div>

        <WhatsAppMockup preview={preview} guestName={preview?.guest_name} loading={previewLoading} error={previewError} />

        {previewError && !previewLoading && (
          <div className="mt-3 px-3 py-2 rounded-lg bg-rose-50 border border-rose-100 text-[11px] text-rose-600 leading-relaxed" data-testid="wa-preview-error">
            Não foi possível carregar a pré-visualização. Verifique sua conexão e tente novamente.
          </div>
        )}

        {preview && (
          <div className="mt-3 px-3 py-2 rounded-lg bg-white/70 border border-zinc-100 text-[11px] text-zinc-500 leading-relaxed">
            Exibindo como <span className="font-medium text-zinc-700">{preview.guest_name}</span> verá.
            {selectedInvites.length > 1 && ` (+${selectedInvites.length - 1} outros)`}
          </div>
        )}
      </div>
    </div>
  );
}

/* ============================================================================
 *  Sub: Location Card
 * ========================================================================= */
function LocationCard({ locMeta, waCaps, value, onChange, onGoToSettings }) {
  const hasAddress = !!locMeta?.has_address;
  const hasCoords = !!locMeta?.has_coordinates;
  const supportsNative = !!locMeta?.supports_native && !!waCaps?.supports_location;

  const buildMapsUrl = () => {
    if (!locMeta) return null;
    if (locMeta.latitude != null && locMeta.longitude != null) {
      return `https://www.google.com/maps?q=${locMeta.latitude},${locMeta.longitude}`;
    }
    if (locMeta.address) {
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(locMeta.address)}`;
    }
    return null;
  };

  const handlePickLink = () => {
    if (!hasAddress) return;
    onChange(value?.mode === "maps_link" ? null : {
      mode: "maps_link",
      address: locMeta.address,
      name: locMeta.name,
      latitude: locMeta.latitude ?? null,
      longitude: locMeta.longitude ?? null,
    });
  };
  const handlePickNative = () => {
    if (!supportsNative) return;
    onChange(value?.mode === "native" ? null : {
      mode: "native",
      address: locMeta.address,
      name: locMeta.name,
      latitude: locMeta.latitude,
      longitude: locMeta.longitude,
    });
  };

  return (
    <div className="rounded-2xl border border-zinc-200/70 p-4 bg-white space-y-3" data-testid="wa-composer-location">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg bg-sky-500/10 text-sky-600 flex items-center justify-center">
            <MapPin size={15} />
          </div>
          <div>
            <p className="text-[13px] font-semibold text-zinc-900 tracking-tight">Localização do evento</p>
            <p className="text-[11px] text-zinc-500">
              {hasAddress ? "Anexe a localização para ajudar os convidados a chegarem" : "Cadastre o endereço em Configurações para enviar"}
            </p>
          </div>
        </div>
        {value && (
          <button
            type="button"
            onClick={() => onChange(null)}
            className="text-[11.5px] text-zinc-500 hover:text-rose-600 inline-flex items-center gap-1"
            data-testid="wa-location-remove"
          >
            <Trash2 size={12} /> remover
          </button>
        )}
      </div>

      {!hasAddress ? (
        <button
          type="button"
          onClick={onGoToSettings}
          className="w-full rounded-xl border-2 border-dashed border-zinc-200 hover:border-zinc-400 bg-zinc-50/50 hover:bg-zinc-50 transition-all px-4 py-4 flex flex-col items-center justify-center gap-1 text-zinc-600"
          data-testid="wa-location-go-settings"
        >
          <MapPin size={16} />
          <span className="text-[12.5px] font-medium">Cadastrar endereço do evento</span>
          <span className="text-[10.5px] text-zinc-400">Configurações → Local do evento</span>
        </button>
      ) : (
        <>
          <div className="rounded-xl bg-zinc-50/60 border border-zinc-100 px-3 py-2.5">
            <p className="text-[12px] font-medium text-zinc-800 tracking-tight">
              {locMeta.name || "Local do evento"}
            </p>
            <p className="text-[11.5px] text-zinc-500 leading-snug mt-0.5">{locMeta.address}</p>
            {buildMapsUrl() && (
              <a
                href={buildMapsUrl()}
                target="_blank"
                rel="noreferrer"
                className="mt-1.5 inline-flex items-center gap-1 text-[10.5px] text-sky-600 hover:underline"
              >
                <ExternalLink size={10} /> Abrir no Maps
              </a>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <button
              type="button"
              onClick={handlePickLink}
              className={`group relative rounded-xl border p-3 text-left transition-all ${
                value?.mode === "maps_link"
                  ? "border-zinc-900 bg-brand-deep text-white"
                  : "border-zinc-200 bg-white hover:border-zinc-300"
              }`}
              data-testid="wa-location-mode-link"
            >
              <div className="flex items-center gap-2">
                <Link2 size={13} className={value?.mode === "maps_link" ? "text-white" : "text-sky-600"} />
                <p className={`text-[12.5px] font-semibold tracking-tight ${value?.mode === "maps_link" ? "text-white" : "text-zinc-900"}`}>
                  Link Google Maps
                </p>
              </div>
              <p className={`mt-1 text-[10.5px] leading-snug ${value?.mode === "maps_link" ? "text-white/70" : "text-zinc-500"}`}>
                Adiciona o link clicável ao final da mensagem.
              </p>
            </button>

            <button
              type="button"
              onClick={handlePickNative}
              disabled={!supportsNative}
              className={`group relative rounded-xl border p-3 text-left transition-all ${
                !supportsNative
                  ? "border-zinc-200 bg-zinc-50/60 opacity-60 cursor-not-allowed"
                  : value?.mode === "native"
                  ? "border-zinc-900 bg-brand-deep text-white"
                  : "border-zinc-200 bg-white hover:border-zinc-300"
              }`}
              data-testid="wa-location-mode-native"
              title={!supportsNative ? "Disponível somente com endereço geocodificado" : ""}
            >
              <div className="flex items-center gap-2">
                <MapPin size={13} className={value?.mode === "native" ? "text-white" : "text-emerald-600"} />
                <p className={`text-[12.5px] font-semibold tracking-tight ${value?.mode === "native" ? "text-white" : "text-zinc-900"}`}>
                  Localização WhatsApp
                </p>
              </div>
              <p className={`mt-1 text-[10.5px] leading-snug ${value?.mode === "native" ? "text-white/70" : "text-zinc-500"}`}>
                {supportsNative
                  ? "Envia um pin nativo do WhatsApp com mini-mapa."
                  : "Disponível quando o endereço tem coordenadas."}
              </p>
            </button>
          </div>

          {value && (
            <p className="text-[10.5px] text-zinc-400 leading-relaxed">
              {value.mode === "maps_link"
                ? "O link será anexado ao final de cada mensagem individualizada."
                : "O pin de localização será enviado logo após a mensagem premium."}
            </p>
          )}
        </>
      )}
    </div>
  );
}


/* ============================================================================
 *  Sub: WhatsApp Mockup
 * ========================================================================= */
function WhatsAppMockup({ preview, guestName, loading, error }) {
  const loc = preview?.location;
  const showNativePin = loc?.mode === "native";
  const mapsThumb = loc?.latitude != null && loc?.longitude != null
    ? `https://staticmap.openstreetmap.de/staticmap.php?center=${loc.latitude},${loc.longitude}&zoom=15&size=400x180&markers=${loc.latitude},${loc.longitude},red-pushpin`
    : null;

  return (
    <div className="rounded-[28px] border border-zinc-200 bg-white shadow-xl overflow-hidden flex-1 flex flex-col">
      {/* Header WhatsApp */}
      <div className="flex items-center gap-3 px-4 py-3 bg-[#075E54] text-white">
        <div className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center text-[13px] font-semibold">
          {(guestName || "C").slice(0, 1).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-medium truncate">{guestName || "Convidado"}</p>
          <p className="text-[10.5px] text-white/70">online</p>
        </div>
      </div>

      {/* Conversa */}
      <div
        className="flex-1 px-4 py-4 flex flex-col gap-2 justify-end"
        style={{
          minHeight: 320,
          backgroundColor: "#ECE5DD",
          backgroundImage:
            "radial-gradient(circle at 20% 30%, rgba(255,255,255,0.35) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(0,0,0,0.04) 0%, transparent 60%)",
        }}
      >
        <div className="max-w-[82%] ml-auto rounded-2xl rounded-br-md overflow-hidden shadow-sm bg-[#DCF8C6]">
          {preview?.media_url && preview?.media_type === "image" && (
            <img
              src={withApiHost(preview.media_url)}
              alt=""
              className="w-full max-h-72 object-cover bg-zinc-200"
              data-testid="wa-preview-image"
            />
          )}
          {preview?.media_url && preview?.media_type === "video" && (
            <div className="aspect-video bg-brand-deep flex items-center justify-center text-white relative" data-testid="wa-preview-video">
              <Video size={28} className="opacity-70" />
              <span className="absolute bottom-1.5 left-2 text-[10px] bg-black/40 px-1.5 py-0.5 rounded">vídeo</span>
            </div>
          )}
          <div className="px-3 py-2">
            <p className="text-[13px] text-zinc-800 whitespace-pre-wrap leading-relaxed" data-testid="wa-preview-text">
              {preview?.message
                ? renderMessageWithLinks(preview.message)
                : loading
                  ? "Carregando pré-visualização…"
                  : error
                    ? "Pré-visualização indisponível no momento."
                    : "Aguardando preview…"}
            </p>
            <p className="text-[9.5px] text-zinc-500 mt-1 text-right">12:34 ✓✓</p>
          </div>
        </div>

        {/* Pin nativo do WhatsApp (mock) — só quando location.mode = native */}
        {showNativePin && (
          <div className="max-w-[82%] ml-auto rounded-2xl rounded-br-md overflow-hidden shadow-sm bg-[#DCF8C6]" data-testid="wa-preview-location-native">
            {mapsThumb ? (
              <img src={mapsThumb} alt="Mapa" className="w-full h-32 object-cover bg-zinc-200" />
            ) : (
              <div className="w-full h-32 bg-gradient-to-br from-emerald-100 to-sky-100 flex items-center justify-center">
                <MapPin size={28} className="text-emerald-700/60" />
              </div>
            )}
            <div className="px-3 py-2">
              <p className="text-[12.5px] font-semibold text-zinc-800 tracking-tight">{loc.name || "Local do evento"}</p>
              {loc.address && <p className="text-[11px] text-zinc-600 leading-snug mt-0.5">{loc.address}</p>}
              <p className="text-[9.5px] text-zinc-500 mt-1 text-right">12:34 ✓✓</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ============================================================================
 *  Sub: Media Thumb
 * ========================================================================= */
function MediaThumb({ media }) {
  if (!media) return null;
  if (media.type === "video") {
    return (
      <div className="rounded-xl overflow-hidden border border-zinc-200">
        <video src={withApiHost(media.url)} controls className="w-full max-h-56 bg-black" />
      </div>
    );
  }
  return (
    <div className="rounded-xl overflow-hidden border border-zinc-200">
      <img src={withApiHost(media.url)} alt="" className="w-full max-h-56 object-cover" />
    </div>
  );
}

/* ============================================================================
 *  Helpers
 * ========================================================================= */
function withApiHost(url) {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  const base = process.env.REACT_APP_BACKEND_URL || "";
  return `${base}${url.startsWith("/") ? "" : "/"}${url}`;
}

function resolveExistingMedia(messageType, wedding) {
  if (!wedding) return null;
  let url = null, t = "";
  if (messageType === "save_the_date") {
    url = wedding.save_the_date_file_url || wedding.save_the_date_url;
    t = wedding.save_the_date_file_type || wedding.save_the_date_type || "";
  } else if (messageType === "invitation") {
    url = wedding.invitation_file_url || wedding.custom_invite_url;
    t = wedding.invitation_file_type || wedding.custom_invite_type || "";
  }
  if (!url) return null;
  const low = url.toLowerCase();
  const isVideo = String(t).toLowerCase().includes("video") || /\.(mp4|mov|webm)$/.test(low);
  return { url, type: isVideo ? "video" : "image" };
}
